import { Card, CardBody, Image } from "@nextui-org/react";
import React from "react";
import Completed from "../../Images/done.svg";
import incompleteIcon from "../../Images/incompleteIcon.svg";
import lastUpdate from "../../Images/lastUpdate.svg";
import { DateFormatorDD } from "../../utils/helper";

/**
 * CardItem Component
 * Reusable card component for displaying metric information with icon, title, and value
 * @param {Object} props - Component props
 * @param {string} props.icon - Icon source URL
 * @param {string} props.title - Title text for the card
 * @param {string|number} props.value - Value to display in the card
 * @returns {JSX.Element} Metric info card
 */
const CardItem = ({ icon, title, value }) => {
  return (
    <Card
      className="bg-[#F8F8F8] h-[75px] w-[250px] cursor-pointer"
      shadow="none"
    >
      <CardBody>
        <div className="flex gap-8">
          <div className="flex items-center gap-4">
            <Image width={50} alt={`${title} icon`} src={icon} />
            <div>
              <div className="text-sm">{title}</div>
              <div className="font-medium">{value}</div>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

/**
 * MetricInfo Component
 * Displays a summary of metric information including status, completion counts, and last update
 * Shows four cards: Status, Total Questions Answered, Total Questions Required, and Last Updated
 * @param {Object} props - Component props
 * @param {Object} props.data - Metric data object containing status, counts, and timestamps
 * @param {string} props.data.Status - Current status of the metric
 * @param {number} props.data.CurrentCount - Number of completed questions
 * @param {number} props.data.RequiredCount - Total number of required questions
 * @param {string} props.data.lastUpdatedAt - Timestamp of last update
 * @returns {JSX.Element} Row of metric info cards
 */
const MetricInfo = ({ data }) => {
  // Extract and format metric data
  const answeredCount = data.CurrentCount || 0;
  const requiredCount = data?.RequiredCount;
  const lastUpdatedAt = data?.lastUpdatedAt
    ? DateFormatorDD(data.lastUpdatedAt)
    : "--";

  return (
    <div className="flex items-center gap-4 my-4">
      {/* Status Card */}
      <CardItem 
        icon={Completed} 
        title="Status" 
        value={data?.Status} 
        aria-label="status-card"
      />
      
      {/* Total Questions Answered Card */}
      <CardItem 
        icon={Completed} 
        title="Total questions answered" 
        value={answeredCount} 
        aria-label="total-question-card"
      />
      
      {/* Total Questions Required Card */}
      <CardItem 
        icon={incompleteIcon} 
        title="Total question Required" 
        value={requiredCount} 
        aria-label="total-required-card"
      />
      
      {/* Last Updated Card */}
      <CardItem 
        icon={lastUpdate} 
        title="Last Updated at" 
        value={lastUpdatedAt} 
        aria-label="last-updated-card"
      />
    </div>
  );
};

export default MetricInfo;
